import { useMemo } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import { QueryStats } from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { SETTLEMENTS_ENDPOINT } from "../../../consts";
import { formatCounterValue, formatNumber, getMonthNames } from "../../../utils";
import CustomDataGrid from "../../../components/ui/customDataGrid/customDataGrid.component";
import SaleSummaryCharts from "../../../components/settlements/saleSummaryCharts/saleSummaryCharts.component";
import { useHasPermission } from "../../../contexts/auth";
import workspaceStyles from "../../../components/ui/workspace/workspace.module.css";
import styles from "./statistics.module.css";

// Lower than the usual 160px table default - this view packs 8 columns side
// by side, so a smaller floor lets the user drag any of them narrower to
// free up room for the ones they actually care about (e.g. widening "Value"
// at the expense of the less-often-checked limit columns).
const STATISTICS_COLUMN_MIN_WIDTH = 90;

const pageCountColumn = (
  field: string,
  headerName: string,
): GridColDef => ({
  field,
  headerName,
  flex: 1,
  minWidth: STATISTICS_COLUMN_MIN_WIDTH,
  align: "right",
  headerAlign: "right",
  valueFormatter: (value) => formatCounterValue(value),
});

export const SettlementsStatistics = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const canViewStatistics = useHasPermission("settlement.can_view_settlement_statistics");
  const monthNames = useMemo(() => getMonthNames(t), [t]);
  const settlementKindParam = useMemo(
    () => new URLSearchParams(location.search).get("settlement_kind") || "",
    [location.search],
  );
  const extraParams = useMemo(
    () => ({ settlement_kind: settlementKindParam || undefined }),
    [settlementKindParam],
  );

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "month",
      headerName: t("Month"),
      flex: 1,
      minWidth: STATISTICS_COLUMN_MIN_WIDTH,
      valueFormatter: (value) => {
        if (!value) return "";
        const date = dayjs(value);
        return `${monthNames[date.month() + 1]} ${date.year()}`;
      },
    },
    {
      field: "total",
      headerName: t("Value (zł)"),
      flex: 1,
      minWidth: STATISTICS_COLUMN_MIN_WIDTH,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => formatNumber(value),
    },
    pageCountColumn("black_pages_used", t("Black pages")),
    pageCountColumn("color_pages_used", t("Color pages")),
    pageCountColumn("black_pages_over_limit", t("Black pages over limit")),
    pageCountColumn("color_pages_over_limit", t("Color pages over limit")),
    pageCountColumn("black_pages_limit", t("Black pages limit")),
    pageCountColumn("color_pages_limit", t("Color pages limit")),
  ], [t, monthNames]);

  if (!canViewStatistics) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        {t("You do not have permission to view statistics.")}
      </Alert>
    );
  }

  return (
    <Box className={`${styles.container} ${workspaceStyles.scrollArea}`}>
      {/* Same settlement-kind filter as the table, so the charts and the rows
          below them always describe the same slice. */}
      <SaleSummaryCharts settlementKind={settlementKindParam || undefined} />
      <Box className={styles.gridWrapper}>
      <CustomDataGrid
        icon={<QueryStats />}
        title={t("Monthly sale summary")}
        url={`${SETTLEMENTS_ENDPOINT}sale-summary-statistics/`}
        gridSettingsKey="SETTLEMENTS_STATISTICS"
        columns={columns}
        extraParams={extraParams}
        initialSortModel={[{ field: "month", sort: "desc" }]}
        dataGridProps={{ pageSizeOptions: [10, 25, 50], disableColumnFilter: true }}
      />
      </Box>
    </Box>
  );
};
